import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { codes, codes as methodCodes, statusShortCode } from 'constants/payment';
import { userShape, paymentsShape, customerShape, legShape, passengersShape, productsShape } from 'constants/propTypes';

import { customerPresent } from '../Payments.utils';
import { standardPaymentLabels } from '../Payments.constants';

import BusinessAccountPayment from '../SplitPaymentMethods/BusinessAccountPayment';
import CashPayment from '../SplitPaymentMethods/CashPayment';
import CustomerNotPresentCardPayment from '../SplitPaymentMethods/CustomerNotPresentCardPayment';
import CustomerPresentCardPayment from '../SplitPaymentMethods/CustomerPresentCardPayment';
import StandardPayment from '../SplitPaymentMethods/StandardPayment';
import VoucherPayment from '../SplitPaymentMethods/VoucherPayment';
import EVoucherCancelPayment from '../SplitPaymentMethods/EVoucherCancelPayment';
import WriteOffPayment from '../SplitPaymentMethods/WriteOffPayment';
import PaperVoucherPayment from '../SplitPaymentMethods/PaperVoucherPayment';
import SocialMediaPayment from '../SplitPaymentMethods/SocialMediaPayment';
import LoyaltyPoints from '../SplitPaymentMethods/LoyaltyPoints';

const SplitPaymentMethod = ({
  reference,
  currency,
  customer,
  addPayment,
  createPaymentLink,
  currentAmount,
  isOnHold,
  loading,
  isPackage,
  hasHotel,
  maximumAmount,
  methodCode,
  onAmountChange,
  payAndConfirm,
  takeDeposit,
  user,
  isPutOnHoldWithDeposit,
  isCancelVoucherRoute = false,
  payments,
  outbound,
  inbound,
  passengers,
  productFamilies = [],
  additionalDetails = [],
  isExchange = false,
  isExtrasOnly = false,
  isInAfterSales = false,
  status,
  isGroup = false
}) => {
  const { offices, email } = user;
  const isCustomerPresent = customerPresent({ offices });

  const havePendingSocialMediaPayment =
    payments?.some((payment) => payment.paymentStatus === statusShortCode.pending && payment.method === codes.SOCIAL_MEDIA) || false;
  if (isCancelVoucherRoute) {
    return (
      <EVoucherCancelPayment
        addPayment={addPayment}
        currency={currency}
        isOnHold={isOnHold}
        loading={loading}
        method="Evoucher"
        payAndConfirm={payAndConfirm}
        reference={reference}
        takeDeposit={takeDeposit}
        totalPriceToBePaid={maximumAmount}
        user={user}
        isCancelVoucherRoute={isCancelVoucherRoute}
      />
    );
  }

  if (methodCode === methodCodes.CASH) {
    return (
      <CashPayment
        addPayment={addPayment}
        bookingReference={reference}
        currency={currency}
        currentAmount={currentAmount}
        isOnHold={isOnHold}
        loading={loading}
        onAmountChange={onAmountChange}
        payAndConfirm={payAndConfirm}
        takeDeposit={takeDeposit}
        reference={reference}
        totalPriceToBePaid={maximumAmount}
        isPutOnHoldWithDeposit={isPutOnHoldWithDeposit}
      />
    );
  }

  if (methodCode === methodCodes.BUSINESS_ACCOUNT) {
    return (
      <BusinessAccountPayment
        addPayment={addPayment}
        currency={currency}
        currentAmount={currentAmount}
        loading={loading}
        isOnHold={isOnHold}
        onAmountChange={onAmountChange}
        payAndConfirm={payAndConfirm}
        takeDeposit={takeDeposit}
        reference={reference}
        totalPriceToBePaid={maximumAmount}
        isPutOnHoldWithDeposit={isPutOnHoldWithDeposit}
      />
    );
  }

  if (methodCode === methodCodes.CARD && isCustomerPresent) {
    return (
      <CustomerPresentCardPayment
        currency={currency}
        currentAmount={currentAmount}
        customer={customer}
        isOnHold={isOnHold}
        isSplitPayment
        onAmountChange={onAmountChange}
        reference={reference}
        totalPriceToBePaid={maximumAmount}
        user={user}
        productFamilies={productFamilies}
        passengers={passengers}
        isExchange={isExchange}
        isPutOnHoldWithDeposit={isPutOnHoldWithDeposit}
      />
    );
  }

  if (methodCode === methodCodes.CARD && !isCustomerPresent) {
    return (
      <CustomerNotPresentCardPayment
        totalPriceToBePaid={maximumAmount}
        reference={reference}
        currency={currency}
        currentAmount={currentAmount}
        isOnHold={isOnHold}
        isPackage={isPackage}
        hasHotel={hasHotel}
        onAmountChange={onAmountChange}
        customer={customer}
        outbound={outbound}
        inbound={inbound}
        passengers={passengers}
        productFamilies={productFamilies}
        offices={offices}
        userEmail={email}
        isPutOnHoldWithDeposit={isPutOnHoldWithDeposit}
        isGroup={isGroup}
      />
    );
  }

  if (methodCode === methodCodes.WRITE_OFF) {
    return (
      <WriteOffPayment
        addPayment={addPayment}
        currency={currency}
        currentAmount={currentAmount}
        isOnHold={isOnHold}
        loading={loading}
        method={methodCode}
        onAmountChange={onAmountChange}
        payAndConfirm={payAndConfirm}
        reference={reference}
        takeDeposit={takeDeposit}
        totalPriceToBePaid={maximumAmount}
        isPutOnHoldWithDeposit={isPutOnHoldWithDeposit}
      />
    );
  }

  if (methodCode === methodCodes.PAPER_VOUCHER) {
    return (
      <PaperVoucherPayment
        addPayment={addPayment}
        currency={currency}
        currentAmount={currentAmount}
        isOnHold={isOnHold}
        loading={loading}
        method={methodCode}
        onAmountChange={onAmountChange}
        payAndConfirm={payAndConfirm}
        reference={reference}
        takeDeposit={takeDeposit}
        totalPriceToBePaid={maximumAmount}
        isPutOnHoldWithDeposit={isPutOnHoldWithDeposit}
      />
    );
  }

  if (methodCode === methodCodes.SOCIAL_MEDIA) {
    return (
      <SocialMediaPayment
        addPayment={addPayment}
        currency={currency}
        currentAmount={currentAmount}
        isOnHold={isOnHold}
        loading={loading}
        method={methodCode}
        onAmountChange={onAmountChange}
        payAndConfirm={payAndConfirm}
        reference={reference}
        takeDeposit={takeDeposit}
        totalPriceToBePaid={maximumAmount}
        isPutOnHoldWithDeposit={isPutOnHoldWithDeposit}
        isExtrasOnly={isExtrasOnly}
        createPaymentLink={createPaymentLink}
        havePendingSocialMediaPayment={havePendingSocialMediaPayment}
        isInAfterSales={isInAfterSales}
        status={status}
      />
    );
  }

  if (methodCode === methodCodes.EVOUCHER) {
    return (
      <VoucherPayment
        addPayment={addPayment}
        currency={currency}
        currentAmount={currentAmount}
        isOnHold={isOnHold}
        loading={loading}
        method={methodCode}
        payAndConfirm={payAndConfirm}
        reference={reference}
        takeDeposit={takeDeposit}
        totalPriceToBePaid={maximumAmount}
        user={user}
        payments={payments}
        isPutOnHoldWithDeposit={isPutOnHoldWithDeposit}
      />
    );
  }

  if (methodCode === methodCodes.LOYALTY_POINTS) {
    return (
      <LoyaltyPoints
        currency={currency}
        isOnHold={isOnHold}
        loading={loading}
        onAmountChange={onAmountChange}
        reference={reference}
        totalPriceToBePaid={maximumAmount}
        payments={payments}
        additionalDetails={additionalDetails}
      />
    );
  }

  return (
    <StandardPayment
      addPayment={addPayment}
      currency={currency}
      currentAmount={currentAmount}
      isOnHold={isOnHold}
      loading={loading}
      method={methodCode}
      onAmountChange={onAmountChange}
      payAndConfirm={payAndConfirm}
      reference={reference}
      takeDeposit={takeDeposit}
      title={<FormattedMessage id={standardPaymentLabels[methodCode]} />}
      totalPriceToBePaid={maximumAmount}
      isPutOnHoldWithDeposit={isPutOnHoldWithDeposit}
    />
  );
};

SplitPaymentMethod.propTypes = {
  addPayment: PropTypes.func,
  createPaymentLink: PropTypes.func,
  currentAmount: PropTypes.string,
  isPackage: PropTypes.bool,
  hasHotel: PropTypes.bool,
  isOnHold: PropTypes.bool,
  isPutOnHoldWithDeposit: PropTypes.bool,
  loading: PropTypes.bool,
  maximumAmount: PropTypes.number,
  methodCode: PropTypes.string,
  onAmountChange: PropTypes.func,
  payAndConfirm: PropTypes.func,
  takeDeposit: PropTypes.func,
  user: userShape.isRequired,
  customer: customerShape,
  reference: PropTypes.string,
  currency: PropTypes.string,
  isCancelVoucherRoute: PropTypes.bool,
  payments: paymentsShape,
  outbound: PropTypes.arrayOf(legShape),
  inbound: PropTypes.arrayOf(legShape),
  passengers: passengersShape,
  productFamilies: productsShape,
  additionalDetails: PropTypes.arrayOf(
    PropTypes.shape({
      itemRef: PropTypes.string,
      key: PropTypes.string,
      value: PropTypes.string
    })
  ),
  isExchange: PropTypes.bool,
  isExtrasOnly: PropTypes.bool,
  isInAfterSales: PropTypes.bool,
  status: PropTypes.string,
  isGroup: PropTypes.bool
};

export default SplitPaymentMethod;
